import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import useLivePoll from "@/hooks/useLivePoll";
import LiveDot from "@/components/LiveDot";
import { Stethoscope, Hourglass, CheckCircle2, AlertTriangle } from "lucide-react";

const Tab = ({ active, onClick, label, count, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-2 text-sm font-semibold transition-colors ${
      active ? "border-b-2 border-[#134e4a] text-[#134e4a]" : "border-b-2 border-transparent text-slate-500 hover:text-slate-800"
    }`}
  >
    {label}
    {typeof count === "number" && <span className="num ml-1.5 text-xs text-slate-500">{count}</span>}
  </button>
);

export default function MyWorklist() {
  const { user, can } = useAuth();
  const [scope, setScope] = useState("mine");
  const [rows, setRows] = useState([]);
  const [signed, setSigned] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async (silent = false) => {
    try {
      const [w, s] = await Promise.all([
        api.get("/worklist", { params: { scope } }),
        api.get("/worklist/summary"),
      ]);
      setRows(w.data);
      setSummary(s.data);
    } catch (e) { if (!silent) toast.error(apiError(e)); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);
  const lastSync = useLivePoll(() => load(true), 6000);

  useEffect(() => {
    if (!user?.id) return;
    api.get("/cases", { params: { pathologist_id: user.id, status: "Sign-Out", limit: 50 } })
      .then((r) => setSigned(r.data.filter((c) => c.signed_out_at).slice(0, 8)))
      .catch(() => setSigned([]));
  }, [user?.id, lastSync]);

  const target = summary?.tat_target_hours || 48;
  const ready = useMemo(() => rows.filter((r) => !r.blocked), [rows]);
  const waiting = useMemo(() => rows.filter((r) => r.blocked), [rows]);

  const claim = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/cases/${row.id}/claim`);
      toast.success(`${row.accession_number} added to your worklist`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const Table = ({ list, testid, showClaim }) => (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[960px] text-sm" data-testid={testid}>
        <thead className="bg-white text-left text-xs uppercase tracking-wide text-slate-500">
          <tr className="border-b border-slate-200">
            <th className="px-3 py-2">Priority</th><th className="px-3 py-2">Case</th><th className="px-3 py-2">Patient</th>
            <th className="px-3 py-2">Specimen</th><th className="px-3 py-2 text-right">Age</th>
            <th className="px-3 py-2 text-right">Blocks / Slides</th><th className="px-3 py-2">Stage</th>
            <th className="px-3 py-2">Flags</th><th className="px-3 py-2">Assigned</th><th className="px-3 py-2 text-right">Open</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r) => {
            const overdue = r.age_hours > target;
            return (
              <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`worklist-row-${r.id}`}>
                <td className="px-3 py-2">
                  {r.priority === "STAT"
                    ? <span className="bg-red-800 px-1.5 py-0.5 text-[11px] font-bold text-white" data-testid={`worklist-stat-${r.id}`}>STAT</span>
                    : <span className="text-slate-500">Routine</span>}
                </td>
                <td className="px-3 py-2">
                  <Link to={`/cases/${r.id}`} className="font-mono font-semibold text-[#134e4a] hover:underline">{r.accession_number}</Link>
                </td>
                <td className="px-3 py-2 text-slate-800">{r.patient_name}</td>
                <td className="px-3 py-2 text-slate-700">{r.specimen_type || "—"}</td>
                <td className={`num px-3 py-2 text-right font-semibold ${overdue ? "text-red-800" : "text-slate-700"}`}
                    data-testid={`worklist-age-${r.id}`}>
                  {r.age_days >= 1 ? `${r.age_days}d` : `${r.age_hours}h`}
                </td>
                <td className="num px-3 py-2 text-right text-slate-700">{r.block_count} / {r.slide_count}</td>
                <td className="px-3 py-2 text-slate-700">{r.status}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {r.pending_recuts > 0 && (
                      <span className="bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900" data-testid={`worklist-recut-${r.id}`}>
                        {r.pending_recuts} recut pending
                      </span>
                    )}
                    {r.slide_count === 0 && (
                      <span className="bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700">No slides</span>
                    )}
                    {(r.synoptic_status === "incomplete" || r.synoptic_status === "not_started") && (
                      <span className="bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`worklist-synoptic-${r.id}`}>
                        Synoptic gaps
                      </span>
                    )}
                    {overdue && (
                      <span className="bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold text-red-800">Over {target}h</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.pathologist_name || "Unassigned"}</td>
                <td className="px-3 py-2 text-right">
                  {showClaim && can("signout") ? (
                    <Button size="sm" disabled={busy === r.id} onClick={() => claim(r)}
                      data-testid={`worklist-claim-${r.id}`} className="h-9 bg-[#134e4a] hover:bg-[#0f3f3c]">Claim</Button>
                  ) : (
                    <Link to={`/cases/${r.id}`} className="text-sm font-semibold text-[#134e4a] hover:underline">Read →</Link>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const Section = ({ label, icon: Icon, count, children, testid }) => (
    <section className="border border-slate-200" data-testid={testid}>
      <header className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <Icon size={15} className="text-[#134e4a]" />
        <h2 className="text-base font-semibold text-slate-800 md:text-lg">{label}</h2>
        <span className="num ml-auto text-sm font-semibold text-slate-600">{count}</span>
      </header>
      {children}
    </section>
  );

  return (
    <div className="space-y-6" data-testid="my-worklist-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">My Worklist</h1>
        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
          {summary ? `${summary.ready} ready to read · ${summary.stat} STAT · ${summary.overdue} over ${target}h` : "Loading…"}
          <LiveDot lastSync={lastSync} testid="worklist-live-indicator" />
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        <Tab active={scope === "mine"} onClick={() => setScope("mine")} label="Mine" count={summary?.assigned} testid="worklist-tab-mine" />
        <Tab active={scope === "unassigned"} onClick={() => setScope("unassigned")} label="Unassigned pool" count={summary?.unassigned} testid="worklist-tab-unassigned" />
        {(user?.role === "admin" || user?.role === "supervisor") && (
          <Tab active={scope === "all"} onClick={() => setScope("all")} label="All open" testid="worklist-tab-all" />
        )}
      </div>

      <Section label={scope === "unassigned" ? "Unassigned — ready to claim" : "Ready to read"} icon={Stethoscope} count={ready.length} testid="worklist-ready">
        {ready.length === 0
          ? <p className="px-3 py-6 text-center text-sm text-slate-500">Nothing ready right now.</p>
          : <Table list={ready} testid="worklist-ready-table" showClaim={scope === "unassigned"} />}
      </Section>

      <Section label="Awaiting slides or recuts" icon={Hourglass} count={waiting.length} testid="worklist-waiting">
        {waiting.length === 0
          ? <p className="px-3 py-6 text-center text-sm text-slate-500">Nothing waiting on histology.</p>
          : <Table list={waiting} testid="worklist-waiting-table" showClaim={scope === "unassigned"} />}
      </Section>

      {scope === "mine" && (
        <Section label="Recently signed out" icon={CheckCircle2} count={signed.length} testid="worklist-signed">
          {signed.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-slate-500">No recent sign-outs.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {signed.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm" data-testid={`worklist-signed-${c.id}`}>
                  <Link to={`/cases/${c.id}`} className="font-mono font-semibold text-[#134e4a] hover:underline">{c.accession_number}</Link>
                  <span className="text-slate-800">{c.patient_name}</span>
                  <span className="num ml-auto text-xs text-slate-500">{fmtDateTime(c.signed_out_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {summary?.blocked > 0 && (
        <p className="flex items-center gap-2 text-xs text-amber-800" data-testid="worklist-blocked-note">
          <AlertTriangle size={14} /> {summary.blocked} of your cases are still waiting on histology.
        </p>
      )}
    </div>
  );
}
